import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2,
    Users,
    Plus,
    Search,
    Edit2,
    Power,
    UserPlus,
    X,
    CheckCircle,
    AlertCircle,
    LayoutDashboard,
    Sun,
    Moon,
    LogOut
} from 'lucide-react';
import { fetchAuth } from '../../utils/api';
import { motion, AnimatePresence } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { logout } from '../../utils/auth';

export default function SuperAdminDashboard() {
    const [companies, setCompanies] = useState([]);
    const [isCreateCompanyModalOpen, setIsCreateCompanyModalOpen] = useState(false);
    const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
    const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterKey, setFilterKey] = useState('ALL');
    const [theme, setTheme] = useState(() => {
        try {
            return localStorage.getItem('superadmin_theme') || 'light';
        } catch {
            return 'light';
        }
    });
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const navigate = useNavigate();

    // Form States
    const [newCompany, setNewCompany] = useState({ name: '', rut: '' });
    const [editCompany, setEditCompany] = useState({ name: '', rut: '' });
    const [newAdmin, setNewAdmin] = useState({ name: '', email: '', password: '' });
    const [isDetailOpen, setIsDetailOpen] = useState(false)
    const [detailCompany, setDetailCompany] = useState(null)
    const [detailAdmins, setDetailAdmins] = useState([])
    const [detailUsersTotal, setDetailUsersTotal] = useState(0)
    const [detailLoading, setDetailLoading] = useState(false)

    useEffect(() => {
        const loadCompanies = async () => {
            try {
                const data = await fetchAuth('/api/superadmin/empresas/');
                const list = (data.empresas || []).map(e => ({
                    id: e.id_empresa,
                    name: e.nombre,
                    rut: e.rut,
                    status: e.estado,
                    createdAt: e.fecha_creacion,
                    adminCreated: !!e.admin_created,
                    subsEstado: e.suscripcion_estado || null,
                    subsProximoPago: e.suscripcion_proximo_pago || null,
                    subsPeriodoFin: e.suscripcion_periodo_fin || null,
                    subsMontoAnual: e.suscripcion_monto_anual || null,
                    subsMoneda: e.suscripcion_moneda || null,
                    pagosAprobados: e.pagos_aprobados || 0,
                    pagosTotal: e.pagos_total || 0
                }));
                setCompanies(list);
            } catch {}
        };
        loadCompanies();
    }, []);

    const fmtDate = (s) => {
        if (!s) return '—'
        try {
            const d = new Date(String(s))
            return d.toISOString().slice(0,10)
        } catch {
            return String(s).slice(0,10)
        }
    }

    const fmtRutInput = (v) => {
        const raw = String(v || '').replace(/[^0-9kK]/g, '').toUpperCase()
        if (!raw) return ''
        if (raw.length === 1) return raw
        const dv = raw.slice(-1)
        let body = raw.slice(0, -1).slice(0, 8)
        let out = ''
        while (body.length > 3) {
            out = '.' + body.slice(-3) + out
            body = body.slice(0, -3)
        }
        out = body + out
        return `${out}-${dv}`
    }

    useEffect(() => {
        try {
            localStorage.setItem('superadmin_theme', theme);
        } catch {}
    }, [theme]);

    const handleLogoutClick = () => {
        setShowLogoutModal(true);
    };

    const handleConfirmLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch {}
        finally {
            setShowLogoutModal(false);
        }
    };

    const handleCancelLogout = () => {
        setShowLogoutModal(false);
    };

    const handleCreateCompany = async (e) => {
        e.preventDefault();
        try {
            const payload = { nombre: newCompany.name, rut: newCompany.rut };
            const resp = await fetchAuth('/api/superadmin/empresas/crear/', { method: 'POST', body: JSON.stringify(payload) });
            const e = resp.empresa || {};
            const company = {
                id: e.id_empresa,
                name: e.nombre,
                rut: e.rut,
                status: e.estado || 'ACTIVO',
                createdAt: e.fecha_creacion,
                adminCreated: false
            };
            setCompanies(prev => [...prev, company]);
            setIsCreateCompanyModalOpen(false);
            setNewCompany({ name: '', rut: '' });
        } catch {}
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        try {
            const payload = { nombre: newAdmin.name, email: newAdmin.email, password: newAdmin.password };
            await fetchAuth(`/api/superadmin/empresas/${selectedCompany.id}/crear-admin/`, { method: 'POST', body: JSON.stringify(payload) });
            setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? { ...c, adminCreated: true } : c));
            setIsAdminModalOpen(false);
            setNewAdmin({ name: '', email: '', password: '' });
            setSelectedCompany(null);
        } catch {}
    };

    const toggleCompanyStatus = async (id) => {
        try {
            const resp = await fetchAuth(`/api/superadmin/empresas/${id}/estado/`, { method: 'PUT' });
            const e = resp.empresa || {};
            setCompanies(prev => prev.map(c => c.id === id ? { ...c, status: e.estado || c.status } : c));
        } catch {}
    };

    const openEditModal = (company) => {
        setSelectedCompany(company);
        setEditCompany({ name: company.name, rut: company.rut });
        setIsEditCompanyModalOpen(true);
    };

    const handleUpdateCompany = async (e) => {
        e.preventDefault();
        try {
            const payload = { nombre: editCompany.name, rut: editCompany.rut };
            const resp = await fetchAuth(`/api/superadmin/empresas/${selectedCompany.id}/actualizar/`, { method: 'PUT', body: JSON.stringify(payload) });
            const e = resp.empresa || {};
            setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? { ...c, name: e.nombre || editCompany.name, rut: e.rut || editCompany.rut } : c));
            setIsEditCompanyModalOpen(false);
            setSelectedCompany(null);
        } catch {}
    };

    const openAdminModal = (company) => {
        setSelectedCompany(company);
        setIsAdminModalOpen(true);
    };

    const loadCompanyAdmins = async (companyId) => {
        try {
            setDetailLoading(true)
            const r = await fetchAuth(`/api/superadmin/empresas/${companyId}/admins/`)
            setDetailAdmins(r.admins || [])
            setDetailUsersTotal(Number(r.usuarios_total || 0))
        } catch {
            setDetailAdmins([])
            setDetailUsersTotal(0)
        } finally {
            setDetailLoading(false)
        }
    }

    const openDetail = async (company) => {
        setDetailCompany(company)
        setIsDetailOpen(true)
        await loadCompanyAdmins(company.id)
    }

    const closeDetail = () => {
        setIsDetailOpen(false)
        setDetailCompany(null)
        setDetailAdmins([])
        setDetailUsersTotal(0)
    }

    const toggleAdminState = async (admin) => {
        try {
            const r = await fetchAuth(`/api/superadmin/empresas/${detailCompany.id}/admins/${admin.id_usuario}/estado/`, { method: 'PUT' })
            const u = r.usuario || {}
            setDetailAdmins(prev => prev.map(a => a.id_usuario === admin.id_usuario ? { ...a, estado: u.estado || a.estado } : a))
        } catch {}
    }

    // Filtered Companies
    const filteredCompanies = companies
        .filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.rut.includes(searchTerm))
        .filter(c => {
            if (filterKey === 'ACTIVE') return c.status === 'ACTIVO';
            if (filterKey === 'INACTIVE') return c.status === 'INACTIVO';
            if (filterKey === 'NOADMIN') return !c.adminCreated;
            return true;
        });



    const handleExportPDF = () => {
        const doc = new jsPDF({ unit: 'pt', format: 'a4' });
        doc.setFontSize(14);
        doc.text('Listado de Empresas', 40, 40);
        const rows = companies.map(c => [c.name, c.rut, c.status, c.createdAt, c.adminCreated ? 'Sí' : 'No']);
        autoTable(doc, { head: [['Empresa', 'RUT', 'Estado', 'Creada', 'Admin']], body: rows, startY: 60 });
        doc.save('empresas.pdf');
    };

    return (
        <div className={theme === 'dark' ? 'dark' : ''}>
        <div className="relative min-h-screen bg-gradient-to-br from-white via-orange-50 to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800">
            <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_left,rgba(251,146,60,0.12),transparent_55%)]" />
            <div className="sticky top-0 z-40 bg-white/70 backdrop-blur shadow-sm dark:bg-slate-900/80">
                <div className="max-w-7xl mx-auto px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 shadow-md" />
                        <div className="flex items-baseline gap-2">
                            <span className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">CommitFlow</span>
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-bold rounded-md bg-orange-100 text-orange-700 border border-orange-200 dark:bg-orange-200/30 dark:text-orange-300 dark:border-orange-300/20">SuperAdmin</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setIsCreateCompanyModalOpen(true)} className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg active:scale-95">
                            <Plus className="w-4 h-4" />
                            Nueva Empresa
                        </button>
                        <button onClick={handleExportPDF} className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-indigo-500 hover:from-indigo-700 hover:to-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg active:scale-95">
                            <LayoutDashboard className="w-4 h-4" />
                            Exportar PDF
                        </button>
                        <button
                            onClick={() => setTheme(prev => prev === 'dark' ? 'light' : 'dark')}
                            title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
                            className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 transition dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
                        >
                            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                        </button>
                        <button onClick={handleLogoutClick} className="inline-flex items-center gap-2 border border-red-300 bg-white text-red-700 hover:bg-red-50 px-4 py-2 rounded-lg text-sm font-semibold shadow-md hover:shadow-lg active:scale-95 dark:bg-slate-800 dark:border-red-700 dark:text-red-300 dark:hover:bg-slate-700">
                            <LogOut className="w-4 h-4" />
                            Cerrar Sesión
                        </button>
                    </div>
                </div>
            </div>
            <div className="max-w-7xl mx-auto px-8 py-12">
                <header className="mb-10">
                    <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Panel de SuperAdmin</h1>
                    <p className="mt-2 text-sm text-slate-600 max-w-prose dark:text-slate-300">Gestión centralizada de empresas y administradores de la plataforma.</p>
                </header>

            {/* Stats Overview */}
            <section className="grid gap-8 sm:grid-cols-3 mb-14">
                <div className="group relative rounded-2xl bg-white/90 backdrop-blur p-6 shadow-sm ring-1 ring-slate-200/60 hover:shadow-xl transition duration-300 dark:bg-slate-900/80 dark:ring-slate-700/60">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">TOTAL EMPRESAS</h3>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
                            <Building2 className="w-6 h-6" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-3">
                        <h2 className="text-4xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">{companies.length}</h2>
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Registradas</span>
                    </div>
                </div>

                <div className="group relative rounded-2xl bg-white/90 backdrop-blur p-6 shadow-sm ring-1 ring-slate-200/60 hover:shadow-xl transition duration-300 dark:bg-slate-900/80 dark:ring-slate-700/60">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">EMPRESAS ACTIVAS</h3>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
                            <CheckCircle className="w-6 h-6" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-3">
                        <h2 className="text-4xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">{companies.filter(c => c.status === 'ACTIVO').length}</h2>
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Operativas</span>
                    </div>
                </div>

                <div className="group relative rounded-2xl bg-white/90 backdrop-blur p-6 shadow-sm ring-1 ring-slate-200/60 hover:shadow-xl transition duration-300 dark:bg-slate-900/80 dark:ring-slate-700/60">
                    <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300">EMPRESAS INACTIVAS</h3>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 text-white shadow-md group-hover:scale-105 group-hover:rotate-3 transition">
                            <AlertCircle className="w-6 h-6" />
                        </div>
                    </div>
                    <div className="flex items-baseline gap-3">
                        <h2 className="text-4xl font-extrabold tracking-tight text-slate-800 dark:text-slate-100">{companies.filter(c => c.status === 'INACTIVO').length}</h2>
                        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Suspendidas</span>
                    </div>
                </div>
            </section>

            

            {/* Companies Management */}
            <div className="bg-white/95 rounded-2xl shadow-md overflow-hidden ring-1 ring-slate-200/60 dark:bg-slate-900/80 dark:ring-slate-700/60">
                <div className="p-8 flex flex-col md:flex-row md:items-center justify-between gap-6 bg-slate-50/60 dark:bg-slate-800/40">
                    <div>
                        <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100">Gestión de Empresas</h2>
                        <p className="text-slate-500 text-sm mt-1 dark:text-slate-400">Administra las organizaciones registradas.</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative group">
                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-orange-500 transition-colors dark:text-slate-400 dark:group-focus-within:text-orange-400" />
                            <input
                                type="text"
                                placeholder="Buscar empresa..."
                                className="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent w-64 transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setFilterKey('ALL')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filterKey==='ALL' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700'}`}>Todas</button>
                            <button onClick={() => setFilterKey('ACTIVE')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filterKey==='ACTIVE' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-teal-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700'}`}>Activas</button>
                            <button onClick={() => setFilterKey('INACTIVE')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filterKey==='INACTIVE' ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-100 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700'}`}>Inactivas</button>
                            <button onClick={() => setFilterKey('NOADMIN')} className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filterKey==='NOADMIN' ? 'bg-orange-600 text-white border-orange-600' : 'bg-white text-slate-700 border-slate-300 hover:bg-orange-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:hover:bg-slate-700'}`}>Sin Admin</button>
                        </div>
                    </div>
                </div>

                <div className="grid items-stretch gap-8 sm:grid-cols-2 lg:grid-cols-3 p-6">
                    <AnimatePresence>
                    {filteredCompanies.map((company) => (
                        <motion.div onClick={() => openDetail(company)} key={company.id} initial={{ opacity: 0, y: 8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 8, scale: 0.98 }} transition={{ duration: 0.22, ease: 'easeOut' }} className="group relative rounded-2xl bg-white/90 backdrop-blur p-7 shadow-sm ring-1 ring-slate-200/60 hover:ring-slate-300/70 hover:shadow-xl hover:-translate-y-1 transition cursor-pointer min-h-[220px] h-full flex flex-col dark:bg-slate-900/80 dark:ring-slate-700/60 dark:hover:ring-slate-600/70">
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-orange-100 text-orange-700 font-extrabold flex items-center justify-center">
                                        {company.name.substring(0, 2).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="font-bold text-slate-900 dark:text-slate-100">{company.name}</div>
                                        <div className="text-xs text-slate-500 font-mono dark:text-slate-400">{company.rut}</div>
                                    </div>
                                </div>
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${company.status === 'ACTIVO' ? 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-800/30 dark:text-teal-300 dark:border-teal-700/40' : 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800/40 dark:text-slate-300 dark:border-slate-700/40'}`}>{company.status}</span>
                            </div>
                            <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
                                <div className="text-xs text-slate-500 dark:text-slate-400">Creada: {fmtDate(company.createdAt)}</div>
                                {company.adminCreated ? (
                                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200/60 dark:bg-indigo-800/30 dark:text-indigo-300 dark:border-indigo-700/40">
                                        <Users className="w-3 h-3" /> Admin asignado
                                    </div>
                                ) : (
                                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-orange-50 text-orange-700 border border-orange-200/60 dark:bg-orange-800/30 dark:text-orange-300 dark:border-orange-700/40">
                                        <AlertCircle className="w-3 h-3" /> Sin Admin
                                    </div>
                                )}
                            </div>
                            <div className="mt-4 mb-3 flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${company.subsEstado==='ACTIVA' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : company.subsEstado ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{company.subsEstado || 'Sin suscripción'}</span>
                                <span className="text-xs text-slate-500">Próximo pago: {company.subsProximoPago ? String(company.subsProximoPago).slice(0,10) : '—'}</span>
                            </div>
                            <div className="mt-auto pt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 dark:border-slate-800">
                                {!company.adminCreated && (
                                    <button onClick={(e) => { e.stopPropagation(); openAdminModal(company) }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-orange-700 bg-orange-100 hover:bg-orange-200 transition dark:text-orange-300 dark:bg-orange-900/30 dark:hover:bg-orange-800/40" title="Crear Primer Admin">
                                        <UserPlus className="w-4 h-4" /> Admin
                                    </button>
                                )}
                                <button onClick={(e) => { e.stopPropagation(); openEditModal(company) }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-indigo-700 bg-indigo-100 hover:bg-indigo-200 transition dark:text-indigo-300 dark:bg-indigo-900/30 dark:hover:bg-indigo-800/40" title="Editar Empresa">
                                    <Edit2 className="w-4 h-4" /> Editar
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); toggleCompanyStatus(company.id) }} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition ${company.status === 'ACTIVO' ? 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/30 dark:hover:bg-red-800/40' : 'text-teal-700 bg-teal-100 hover:bg-teal-200 dark:text-teal-300 dark:bg-teal-900/30 dark:hover:bg-teal-800/40'}`} title={company.status === 'ACTIVO' ? 'Desactivar' : 'Activar'}>
                                    <Power className="w-4 h-4" /> {company.status === 'ACTIVO' ? 'Inhabilitar' : 'Activar'}
                                </button>
                            </div>
                        </motion.div>
                    ))}
                    </AnimatePresence>
                </div>

                {filteredCompanies.length === 0 && (
                    <div className="p-12 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-4 dark:bg-slate-800">
                            <Building2 className="w-6 h-6 text-slate-400 dark:text-slate-500" />
                        </div>
                        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">No se encontraron empresas</h3>
                        <p className="text-slate-500 mt-1 dark:text-slate-400">Intenta con otros términos de búsqueda o crea una nueva empresa.</p>
                    </div>
                )}
        </div>

            <AnimatePresence>
            {isDetailOpen && detailCompany && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2, ease: 'easeOut' }} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <motion.div initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.25, ease: 'easeOut' }} className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden dark:bg-slate-900 dark:text-slate-100">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                            <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">Detalle de Empresa</h3>
                            <button onClick={closeDetail} className="text-slate-400 hover:text-slate-600 transition-colors dark:text-slate-400 dark:hover:text-slate-300">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-5">
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Empresa</div>
                                    <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{detailCompany.name}</div>
                                </div>
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">RUT</div>
                                    <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{detailCompany.rut}</div>
                                </div>
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Creada</div>
                                    <div className="text-lg font-bold text-slate-900 dark:text-slate-100">{fmtDate(detailCompany.createdAt)}</div>
                                </div>
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Estado</div>
                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${detailCompany.status === 'ACTIVO' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{detailCompany.status}</span>
                                </div>
                            </div>

                            <div className="grid gap-4 sm:grid-cols-4">
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Suscripción</div>
                                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{detailCompany.subsEstado || 'Sin suscripción'}</div>
                                </div>
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Próximo pago</div>
                                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{detailCompany.subsProximoPago ? String(detailCompany.subsProximoPago).slice(0,10) : '—'}</div>
                                </div>
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Pagos</div>
                                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{detailCompany.pagosAprobados}/{detailCompany.pagosTotal}</div>
                                </div>
                                <div className="rounded-xl bg-white p-4 border border-slate-200/70 shadow-sm dark:bg-slate-800/40 dark:border-slate-700/50">
                                    <div className="text-xs text-slate-500">Usuarios totales</div>
                                    <div className="text-sm font-bold text-slate-900 dark:text-slate-100">{detailUsersTotal}</div>
                                </div>
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">Administradores ({detailAdmins.length})</div>
                                    {detailLoading && <div className="text-xs text-slate-500">Cargando…</div>}
                                </div>
                                <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden shadow-sm dark:bg-slate-900 dark:border-slate-700">
                                    <table className="w-full text-sm">
                                        <thead className="bg-slate-50 dark:bg-slate-800/40">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 dark:text-slate-300">Nombre</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 dark:text-slate-300">Email</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 dark:text-slate-300">Estado</th>
                                                <th className="px-4 py-3 text-left text-xs font-bold text-slate-600 dark:text-slate-300">Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {detailAdmins.length === 0 ? (
                                                <tr>
                                                    <td className="px-4 py-4 text-slate-500" colSpan={4}>Sin administradores</td>
                                                </tr>
                                            ) : detailAdmins.map(a => (
                                                <tr key={a.id_usuario} className="border-t border-slate-100 dark:border-slate-800">
                                                    <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{a.nombre}</td>
                                                    <td className="px-4 py-3 text-slate-900 dark:text-slate-100">{a.email}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border ${a.estado==='ACTIVO' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>{a.estado}</span>
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <button onClick={() => toggleAdminState(a)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition ${a.estado==='ACTIVO' ? 'text-red-700 bg-red-100 hover:bg-red-200' : 'text-teal-700 bg-teal-100 hover:bg-teal-200'}`}>
                                                            <Power className="w-4 h-4" /> {a.estado==='ACTIVO' ? 'Inhabilitar' : 'Activar'}
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
            </AnimatePresence>

        </div>
    </div>

            {/* Create Company Modal */}
            {isCreateCompanyModalOpen && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease]">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[slideUp_0.3s_cubic-bezier(0.4,0,0.2,1)] dark:bg-slate-900 dark:text-slate-100">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                            <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">Nueva Empresa</h3>
                            <button
                                onClick={() => setIsCreateCompanyModalOpen(false)}
                                className="text-slate-400 hover:text-slate-600 transition-colors dark:text-slate-400 dark:hover:text-slate-300"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateCompany} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Nombre de la Empresa</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    placeholder="Ej: Tech Solutions SpA"
                                    value={newCompany.name}
                                    onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">RUT</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    inputMode="text"
                                    maxLength={12}
                                    pattern="[0-9\\.\\-Kk]+"
                                    title="Formato: 12.345.678-5"
                                    placeholder="Ej: 76.123.456-K"
                                    value={newCompany.rut}
                                    onChange={(e) => setNewCompany({ ...newCompany, rut: fmtRutInput(e.target.value) })}
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsCreateCompanyModalOpen(false)}
                                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
                                >
                                    Crear Empresa
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Create Admin Modal */}
            {isAdminModalOpen && selectedCompany && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease]">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[slideUp_0.3s_cubic-bezier(0.4,0,0.2,1)] dark:bg-slate-900 dark:text-slate-100">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                            <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">Crear Administrador</h3>
                            <button
                                onClick={() => {
                                    setIsAdminModalOpen(false);
                                    setSelectedCompany(null);
                                }}
                                className="text-slate-400 hover:text-slate-600 transition-colors dark:text-slate-400 dark:hover:text-slate-300"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleCreateAdmin} className="p-6 space-y-4">
                            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 flex items-start gap-3 dark:bg-orange-900/30 dark:border-orange-700/40">
                                <AlertCircle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
                                <div className="text-sm text-orange-800 dark:text-orange-300">
                                    Estás creando el primer usuario administrador para la empresa <strong>{selectedCompany.name}</strong>.
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Empresa Asignada</label>
                                <input
                                    type="text"
                                    disabled
                                    className="w-full px-3 py-2 border border-slate-200 bg-slate-50 text-slate-500 rounded-lg cursor-not-allowed dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700"
                                    value={selectedCompany.name}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Nombre Completo</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    placeholder="Ej: Juan Pérez"
                                    value={newAdmin.name}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Correo Electrónico</label>
                                <input
                                    type="email"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    placeholder="admin@empresa.com"
                                    value={newAdmin.email}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Contraseña Temporal</label>
                                <input
                                    type="password"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    placeholder="••••••••"
                                    value={newAdmin.password}
                                    onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                                />
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsAdminModalOpen(false);
                                        setSelectedCompany(null);
                                    }}
                                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-orange-600 text-white font-medium rounded-lg hover:bg-orange-700 transition-colors shadow-lg shadow-orange-500/20"
                                >
                                    Crear Admin
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isEditCompanyModalOpen && selectedCompany && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-[fadeIn_0.2s_ease]">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-[slideUp_0.3s_cubic-bezier(0.4,0,0.2,1)] dark:bg-slate-900 dark:text-slate-100">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                            <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">Editar Empresa</h3>
                            <button
                                onClick={() => { setIsEditCompanyModalOpen(false); setSelectedCompany(null); }}
                                className="text-slate-400 hover:text-slate-600 transition-colors dark:text-slate-400 dark:hover:text-slate-300"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateCompany} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">Nombre de la Empresa</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    value={editCompany.name}
                                    onChange={(e) => setEditCompany({ ...editCompany, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1 dark:text-slate-200">RUT</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all bg-white placeholder:text-slate-400 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700 dark:placeholder:text-slate-500"
                                    inputMode="text"
                                    maxLength={12}
                                    pattern="[0-9\\.\\-Kk]+"
                                    title="Formato: 12.345.678-5"
                                    value={editCompany.rut}
                                    onChange={(e) => setEditCompany({ ...editCompany, rut: fmtRutInput(e.target.value) })}
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setIsEditCompanyModalOpen(false); setSelectedCompany(null); }}
                                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-slate-900 text-white font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-lg shadow-slate-900/20"
                                >
                                    Guardar cambios
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showLogoutModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden dark:bg-slate-900 dark:text-slate-100">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 dark:bg-slate-800 dark:border-slate-700">
                            <h3 className="font-bold text-lg text-slate-900 dark:text-slate-100">¿Cerrar Sesión?</h3>
                            <button
                                onClick={handleCancelLogout}
                                className="text-slate-400 hover:text-slate-600 transition-colors dark:text-slate-400 dark:hover:text-slate-300"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="p-6 space-y-4">
                            <p className="text-sm text-slate-600 dark:text-slate-300">¿Estás seguro que deseas cerrar tu sesión?</p>
                            <div className="pt-2 flex gap-3">
                                <button
                                    type="button"
                                    onClick={handleCancelLogout}
                                    className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-50 transition-colors dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmLogout}
                                    className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Sí, Cerrar Sesión
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
